
function compareTableValue(a, b, type = "text") {
  if (type === "number") {
    return (Number(a || 0) - Number(b || 0));
  }
  if (type === "date") {
    return (new Date(a || 0).getTime() || 0) -
           (new Date(b || 0).getTime() || 0);
  }
  return String(a ?? "").localeCompare(String(b ?? ""), "id", {
    numeric: true, sensitivity: "base"
  });
}

function sortTableRows(rows, config) {
  const { key, direction, type = "text" } = config;
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const result = compareTableValue(a?.[key], b?.[key], type);
    return result === 0 ? 0 : result * sign;
  });
}

function SortHead({ label, field, config, onSort, type = "text", className = "" }) {
  const active = config.key === field;
  return (
    <button type="button" onClick={() => onSort(field, type)}
      className={`inline-flex items-center gap-1 font-semibold hover:text-orange-600 ${className}`}
      title={`Urutkan ${label}`}>
      {label}
      <span aria-hidden="true" className={active ? "text-orange-600" : "text-slate-400"}>
        {active ? (config.direction === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </button>
  );
}

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

export default function ReconciliationPage() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc", type: "date" });
  const toggleSort = (key, type = "text") => setSortConfig(prev => ({
    key,
    type,
    direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
  }));

  useEffect(() => {
    api.get(`/reconciliation/payments?period=${period}`).then(({data})=>setData(data));
  }, [period]);

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div data-workspace-redundant-header><h1 className="text-2xl font-extrabold font-display">Rekonsiliasi Pembayaran</h1></div>
          <p className="text-slate-500 text-sm">Cek konsistensi POS amount vs Provider amount (QRIS)</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="recon-period" className="w-40"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Hari Ini</SelectItem>
            <SelectItem value="week">Minggu Ini</SelectItem>
            <SelectItem value="month">Bulan Ini</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500">Total Payment</div>
              <div className="text-2xl font-extrabold num">{data.count}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500">Reconciliation Errors</div>
              <div className={`text-2xl font-extrabold num ${data.errors > 0 ? "text-rose-600" : "text-emerald-600"}`}>{data.errors}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              {data.errors === 0 ? <><CheckCircle2 className="w-8 h-8 text-emerald-500"/><div><div className="font-semibold text-emerald-700">Semua Cocok</div><div className="text-xs text-slate-500">Tidak ada variance</div></div></> :
                <><AlertTriangle className="w-8 h-8 text-amber-500"/><div><div className="font-semibold text-amber-700">Perlu Perhatian</div><div className="text-xs text-slate-500">{data.errors} pembayaran variance</div></div></>}
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Detail Payment</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead><SortHead label="Waktu" field="created_at" type="date" config={sortConfig} onSort={toggleSort} /></TableHead><TableHead><SortHead label="Trx No." field="trx_no" type="text" config={sortConfig} onSort={toggleSort} /></TableHead><TableHead><SortHead label="Provider" field="provider" type="text" config={sortConfig} onSort={toggleSort} /></TableHead>
                  <TableHead><SortHead label="POS Amount" field="pos_amount" type="number" config={sortConfig} onSort={toggleSort} /></TableHead><TableHead><SortHead label="Provider Amount" field="provider_amount" type="number" config={sortConfig} onSort={toggleSort} /></TableHead>
                  <TableHead><SortHead label="Selisih" field="difference" type="number" config={sortConfig} onSort={toggleSort} /></TableHead><TableHead><SortHead label="Status" field="payment_status" type="text" config={sortConfig} onSort={toggleSort} /></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {sortTableRows(data.items, sortConfig).map(r => (
                    <TableRow key={r.payment_id} className={r.reconciliation_error ? "bg-rose-50" : ""}>
                      <TableCell className="text-xs">{new Date(r.created_at).toLocaleString("id-ID")}</TableCell>
                      <TableCell className="font-mono text-xs">{r.trx_no}</TableCell>
                      <TableCell><Badge variant="outline">{r.provider}</Badge></TableCell>
                      <TableCell className="text-right num">{rupiah(r.pos_amount)}</TableCell>
                      <TableCell className="text-right num">{rupiah(r.provider_amount)}</TableCell>
                      <TableCell className={`text-right num ${r.difference !== 0 ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{r.difference === 0 ? "—" : rupiah(r.difference)}</TableCell>
                      <TableCell>
                        {r.reconciliation_error ? <Badge variant="destructive">ERROR</Badge> :
                          <Badge className={r.payment_status === "paid" ? "bg-emerald-500" : "bg-amber-500"}>{r.payment_status?.toUpperCase()}</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!data.items.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada payment untuk periode ini</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
